// FILE: src/components/CoachLayout.js
//
// CoachLayout — the reusable "coach moment" pattern for PM Pixel.
//
// Places the mascot side-by-side with a coaching message (header + supporting
// text + CTA) in two columns, so PM Pixel reads as PART OF the message rather
// than a decorative image floating above it. Avatar sits on the left, larger
// than the inline default, vertically centered against the content. Collapses
// to a single stacked column on narrow screens.
//
//   <CoachLayout pose="notepad" heightPx={220}>
//     <p>eyebrow</p>
//     <h2>Heading</h2>
//     <p>supporting text</p>
//     <button>CTA</button>
//   </CoachLayout>
//
// Intentionally scoped to high-emotion / coaching moments (empty states,
// milestone / achievement headers) — NOT results or dense analytics screens.
// Keep one CoachLayout (one avatar) per screen.
//
// No Redux. Functional component, hooks-free, presentational only.

import React from 'react';
import CoachAvatar from './CoachAvatar';
import mcss from './CoachLayout.module.css';

/**
 * @param {string}  pose         Pose token forwarded to CoachAvatar
 *                               (idle | thumbsup | cheer | notepad | certified | …).
 * @param {number}  [heightPx]   Avatar display height. Defaults to 220 — a
 *                               deliberate "coach presence" size, larger than the
 *                               inline default while staying proportional to the
 *                               96x192 canonical sprite.
 * @param {boolean} [decorative] Forwarded to CoachAvatar (default true).
 * @param {string}  [align]      'center' (default) or 'top' — how the avatar
 *                               aligns against the content column.
 * @param {string}  [contentAlign] 'left' (default) or 'center' — text alignment
 *                               of the content column. Coaching headers read
 *                               left; result summaries (centered score circle)
 *                               read better centered.
 * @param {string}  [testId]     Optional data-testid on the row wrapper.
 * @param {string}  [className]  Optional extra class on the row wrapper.
 * @param {React.ReactNode} children  The coaching content (header, text, CTA).
 */
export default function CoachLayout({
  pose,
  heightPx = 220,
  decorative = true,
  align = 'center',
  contentAlign = 'left',
  testId,
  className = '',
  children,
}) {
  const rowClass = [
    mcss.row,
    align === 'top' ? mcss.alignTop : mcss.alignCenter,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const contentClass = [
    mcss.contentCol,
    contentAlign === 'center' ? mcss.contentCenter : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rowClass} data-testid={testId}>
      <div className={mcss.avatarCol}>
        <CoachAvatar pose={pose} heightPx={heightPx} decorative={decorative} />
      </div>
      <div className={contentClass}>{children}</div>
    </div>
  );
}
